import { create } from 'zustand';

import {
  listWards,
  createWard as apiCreateWard,
  updateWard as apiUpdateWard,
  deactivateWard as apiDeactivateWard,
  listRooms,
  createRoom as apiCreateRoom,
  updateRoom as apiUpdateRoom,
  deactivateRoom as apiDeactivateRoom,
  listBeds,
  createBeds as apiCreateBeds,
  updateBed as apiUpdateBed,
  deactivateBed as apiDeactivateBed,
  setWardDepartments as apiSetWardDepartments,
} from '../services/api/bedInventoryApi';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Bed inventory store (B3 / S1–S7) — Zustand
 *
 * ONE store is the SINGLE SOURCE OF TRUTH for BOTH the S5 builder tree and
 * the S7 read-only bed board (§2.3). Rows keyed by ward_id / room_id / bed_id.
 *
 * Runtime safety (memory rule "no request storms"): `hydrate` is idempotent +
 * single-flight — it fires exactly THREE list calls (wards + rooms + beds in
 * PARALLEL), a repeat call while loaded is a no-op, and concurrent callers
 * share ONE in-flight promise. So a re-rendering consumer effect can never
 * multiply requests. `includeInactive` is a backend filter → flipping it
 * re-hydrates with force.
 *
 * Cascade correctness (single source of truth): after a deactivate / reactivate
 * we RE-HYDRATE (force) rather than locally guessing which descendants the
 * server's parent-inactive `effective_active` resolution hides/restores — the
 * server is the authority for the subtree, the store just mirrors it.
 * ═══════════════════════════════════════════════════════════════════════
 */

const useBedInventoryStore = create((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────
  wards: [],
  rooms: [],
  beds: [],
  isLoading: false,
  error: null,
  includeInactive: false,
  loaded: false,      // "already loaded" guard — repeat hydrates no-op
  _inFlight: null,    // shared in-flight promise (single-flight)

  // ── Hydrate (bounded: single-flight + loaded guard; 3 parallel lists) ──
  hydrate: async ({ force = false } = {}) => {
    const { _inFlight, loaded } = get();
    if (_inFlight) return _inFlight; // concurrent callers share the promise
    if (loaded && !force) {
      return { success: true, data: { wards: get().wards, rooms: get().rooms, beds: get().beds } };
    }
    set({ isLoading: true, error: null });
    const promise = (async () => {
      const include = get().includeInactive;
      const [w, r, b] = await Promise.all([
        listWards({ include_inactive: include }),
        listRooms({ include_inactive: include }),
        listBeds({ include_inactive: include }),
      ]);
      if (w.success && r.success && b.success) {
        set({
          wards: w.data.wards || [],
          rooms: r.data.rooms || [],
          beds: b.data.beds || [],
          isLoading: false,
          loaded: true,
          _inFlight: null,
        });
        return { success: true, data: { wards: w.data.wards, rooms: r.data.rooms, beds: b.data.beds } };
      }
      const err = w.error || r.error || b.error || 'Failed to load bed inventory.';
      set({ error: err, isLoading: false, _inFlight: null });
      return { success: false, error: err };
    })();
    set({ _inFlight: promise });
    return promise;
  },

  // ── Backend filter: re-hydrate on flip ─────────────────────────────
  setIncludeInactive: (value) => {
    set({ includeInactive: !!value });
    return get().hydrate({ force: true });
  },

  // ── Wards ──────────────────────────────────────────────────────────
  createWard: async (body) => {
    const result = await apiCreateWard(body);
    if (result.success) await get().hydrate({ force: true });
    return result;
  },
  updateWard: async (wardId, body) => {
    const result = await apiUpdateWard(wardId, body);
    if (result.success) {
      set((s) => ({
        wards: s.wards.map((w) =>
          w.ward_id === wardId ? { ...w, ...(result.data || body) } : w),
      }));
    }
    return result;
  },
  deactivateWard: async (wardId) => {
    const result = await apiDeactivateWard(wardId);
    if (result.success) await get().hydrate({ force: true }); // reflect cascade
    return result;
  },
  reactivateWard: async (wardId) => {
    const result = await apiUpdateWard(wardId, { active: true });
    if (result.success) await get().hydrate({ force: true }); // restore subtree
    return result;
  },
  // B6 — set the ward's "Visible to" department tags via the departments PUT.
  // On success force ONE re-hydrate so the row's server-derived owner_scope +
  // department_ids (active-only) are refreshed (single forced refetch, bounded).
  setWardDepartments: async (wardId, departmentIds) => {
    const result = await apiSetWardDepartments(wardId, departmentIds);
    if (result.success) await get().hydrate({ force: true });
    return result;
  },

  // ── Rooms (incl. bays) ─────────────────────────────────────────────
  createRoom: async (wardId, body) => {
    const result = await apiCreateRoom(wardId, body);
    if (result.success) await get().hydrate({ force: true });
    return result;
  },
  updateRoom: async (wardId, roomId, body) => {
    const result = await apiUpdateRoom(wardId, roomId, body);
    if (result.success) {
      set((s) => ({
        rooms: s.rooms.map((r) =>
          r.room_id === roomId ? { ...r, ...(result.data || body) } : r),
      }));
    }
    return result;
  },
  deactivateRoom: async (wardId, roomId) => {
    const result = await apiDeactivateRoom(wardId, roomId);
    if (result.success) await get().hydrate({ force: true });
    return result;
  },
  reactivateRoom: async (wardId, roomId) => {
    const result = await apiUpdateRoom(wardId, roomId, { active: true });
    if (result.success) await get().hydrate({ force: true });
    return result;
  },

  // ── Beds (bulk-capable) ────────────────────────────────────────────
  createBeds: async (wardId, roomId, body) => {
    const result = await apiCreateBeds(wardId, roomId, body);
    if (result.success) await get().hydrate({ force: true });
    return result;
  },
  updateBed: async (wardId, roomId, bedId, body) => {
    const result = await apiUpdateBed(wardId, roomId, bedId, body);
    if (result.success) {
      set((s) => ({
        beds: s.beds.map((b) =>
          b.bed_id === bedId ? { ...b, ...(result.data || body) } : b),
      }));
    }
    return result;
  },
  deactivateBed: async (wardId, roomId, bedId) => {
    const result = await apiDeactivateBed(wardId, roomId, bedId);
    if (result.success) await get().hydrate({ force: true });
    return result;
  },
  reactivateBed: async (wardId, roomId, bedId) => {
    const result = await apiUpdateBed(wardId, roomId, bedId, { active: true });
    if (result.success) await get().hydrate({ force: true });
    return result;
  },

  clearError: () => set({ error: null }),

  // Test/utility hook — reset to a clean slate.
  reset: () => set({
    wards: [], rooms: [], beds: [],
    isLoading: false, error: null,
    includeInactive: false, loaded: false, _inFlight: null,
  }),
}));

export default useBedInventoryStore;

/**
 * ═══════════════════════════════════════════════════════════════════════
 * SHARED SELECTORS — single source of the derived ward→room→bed structure,
 * consumed by BOTH the S5 builder tree AND the S7 board (memory rule
 * "single source of truth for derived values"). Never re-derive inline.
 * ═══════════════════════════════════════════════════════════════════════
 */

/**
 * selectBoardTree(state) → ordered ward nodes with their rooms + bays + beds.
 * Shape:
 *   [{ ward,
 *      rooms: [{ room, beds:[bed…] }],   // ordinary (is_bay=false) rooms
 *      bays:  [{ room, beds:[bed…] }] }] // is_bay=true rooms (beds render
 *                                        //   directly under the ward)
 * A ward with a null/dead department is NEVER dropped (empty-set carve-out);
 * a room-less ward renders with empty rooms+bays; a bay bed is never lost —
 * it hangs off its is_bay room grouped under the ward.
 */
export function selectBoardTree(state) {
  const { wards = [], rooms = [], beds = [] } = state || {};
  const bedsByRoom = {};
  for (const b of beds) {
    (bedsByRoom[b.room_id] = bedsByRoom[b.room_id] || []).push(b);
  }
  const roomsByWard = {};
  for (const r of rooms) {
    (roomsByWard[r.ward_id] = roomsByWard[r.ward_id] || []).push(r);
  }
  return wards.map((ward) => {
    const wardRooms = roomsByWard[ward.ward_id] || [];
    const rooms_ = [];
    const bays_ = [];
    for (const room of wardRooms) {
      const node = { room, beds: bedsByRoom[room.room_id] || [] };
      (room.is_bay ? bays_ : rooms_).push(node);
    }
    return { ward, rooms: rooms_, bays: bays_ };
  });
}

/**
 * B7 UNCATEGORIZED sentinel — beds whose room carries no bed-category (or a
 * retired/unresolvable one) land here; they are NEVER dropped from a count and
 * NEVER fake-zeroed. Label resolution ("Uncategorised") happens at render.
 */
export const UNCATEGORIZED = 'UNCATEGORIZED';

/**
 * wardChildCounts(rooms, beds, wardId) → { rooms, beds } directly under a ward,
 * counting EVERY room (bays included). This is the CASCADE-CONFIRM reducer used
 * by `selectChildCounts`: the deactivate confirm answers "what gets hidden", and
 * a bay IS a room row that gets hidden — so bays are counted here on purpose.
 * (The B7 stats room tally is DIFFERENT — it excludes bays; see
 * `selectInventoryStats`. Bay beds are always counted via the denormalized
 * `ward_id` bed attribute — never a rooms-only walk.)
 */
function wardChildCounts(rooms, beds, wardId) {
  return {
    rooms: rooms.filter((r) => r.ward_id === wardId).length,
    beds: beds.filter((b) => b.ward_id === wardId).length,
  };
}

/**
 * selectChildCounts(state, id) → { rooms, beds } that a deactivate would hide.
 * `id` may be a WARD id (→ counts its rooms + all its beds, incl. bay beds via
 * the denormalized `ward_id` bed attribute) or a ROOM id (→ counts that room's
 * beds; rooms:0). Resolved by looking the id up in the ward list first.
 */
export function selectChildCounts(state, id) {
  const { wards = [], rooms = [], beds = [] } = state || {};
  const isWard = wards.some((w) => w.ward_id === id);
  if (isWard) return wardChildCounts(rooms, beds, id);
  return { rooms: 0, beds: beds.filter((b) => b.room_id === id).length };
}

/**
 * selectInventoryStats(state) → the SINGLE SOURCE for every B7 config count.
 * CONFIG capacity only (no occupancy — beds have no state until EPIC C).
 *
 *   { totals:     { wards, rooms, beds },              // org-wide, ACTIVE set
 *     perWard:    { [ward_id]: { rooms, beds } },      // rooms EXCLUDE bays; bay beds incl.
 *     byCategory: [{ category_id, count }] }           // via room.bedcat_id
 *
 * Correctness rules (architect RC-1..6):
 *  - ACTIVE partition on `effective_active` (parent-cascade), NOT raw `active` —
 *    so the strip equals the rendered tiles; the "show deactivated" toggle can
 *    add inactive rows to the store without inflating these active totals.
 *  - ROOM tally EXCLUDES bays (owner decision): a bay (`is_bay` room) is an
 *    open-ward grouping, not a countable room — `totals.rooms` / `perWard[].rooms`
 *    count only active NON-bay rooms. (The cascade-confirm reducer still counts
 *    bays — see `wardChildCounts` — because a bay row does get hidden on deactivate.)
 *  - totals.beds is the DISTINCT count of active beds (physical rows) — never a
 *    Σ over any per-anything bucket (count-each-bed-once). Bay BEDS still count
 *    everywhere (per-ward + totals + by-category), only the ROOM figure drops bays.
 *  - By-category groups active beds by their ROOM's `bedcat_id` (beds carry no
 *    category); a missing/retired id → UNCATEGORIZED bucket, beds never dropped.
 * Every count surface (setup strip, board strip, ward-card count, board per-ward
 * count) consumes THIS — no inline re-derivation (single-source; tooth-chart lesson).
 */
export function selectInventoryStats(state) {
  const { wards = [], rooms = [], beds = [] } = state || {};
  const isActive = (row) => row.effective_active !== false;
  const activeWards = wards.filter(isActive);
  // ROOM tally excludes bays (owner decision) — a bay is not a countable room.
  const activeNonBayRooms = rooms.filter((r) => isActive(r) && !r.is_bay);
  const activeBeds = beds.filter(isActive);

  // room_id → bedcat_id (from ALL rooms; an active bed's room is active anyway).
  const bedcatByRoom = {};
  for (const r of rooms) bedcatByRoom[r.room_id] = r.bedcat_id;

  const perWard = {};
  for (const w of activeWards) {
    perWard[w.ward_id] = {
      rooms: activeNonBayRooms.filter((r) => r.ward_id === w.ward_id).length,
      beds: activeBeds.filter((b) => b.ward_id === w.ward_id).length,
    };
  }

  const catCounts = {};
  for (const b of activeBeds) {
    const key = bedcatByRoom[b.room_id] || UNCATEGORIZED;
    catCounts[key] = (catCounts[key] || 0) + 1;
  }
  const byCategory = Object.entries(catCounts).map(
    ([category_id, count]) => ({ category_id, count }),
  );

  return {
    totals: { wards: activeWards.length, rooms: activeNonBayRooms.length, beds: activeBeds.length },
    perWard,
    byCategory,
  };
}

/**
 * compositeBedLabel(ward, room, bed) → S7 AC5 human-readable identity.
 * Normal room → "{ward} · {room} · {bed}". Bay room (is_bay) → the room name is
 * an internal "General Bay" grouping, omitted → "{ward} · {bed}". C2 reuses.
 */
export function compositeBedLabel(ward, room, bed) {
  const wardName = ward?.name || '';
  const bedLabel = bed?.label || '';
  if (room && !room.is_bay && room.name) {
    return `${wardName} · ${room.name} · ${bedLabel}`;
  }
  return `${wardName} · ${bedLabel}`;
}
